import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessage from '../../../../components/messages/ErrorMessage';
import { useCompanyContext } from '../../../../context/CompanyContext';
import APOpenTable, { APOpenTableProps } from './APOpenTable';
import { useFormContext } from 'react-hook-form';
import {
  fetchAccountsPayablesData,
  fetchSubmittedPaymentBatches,
} from '../../../../services/accounting/AccountsPayablesService';
import APUnapprovedTable, { APUnapprovedTableProps } from './APUnapprovedTable';
import { fetchAccountsPayablesFilters } from '../APFilters';
import { processPayableList } from '../apUtil';
import checkPermissions from '../../../../HOCs/CheckPermissions';
import APYardiOpenTable from './APYardiOpenTable';
import { CompanySource } from '../../../../Interfaces/admin/User.interfaces';
import {
  BatchType,
  PaymentBatch,
  PaymentBatchSummary,
} from '../../../../Interfaces/Accounting/AccountsPayables.interfaces';
import { getSource } from '@/src/utils/company';
import { useRolesAccessContext } from '../../../../context/RolesAccessContext';
import { ProcessContextProvider } from '../../../../context/ProcessContext';
import ApprovalHistory from '../../../../components/approvals/ApprovalHistory';

type APOpenTableContainerProps = {
  createBatch?: boolean;
  changeCreateBatch?: (data: boolean) => void;
};

const APOpenTableContainer = React.forwardRef<
  HTMLDivElement,
  APOpenTableContainerProps
>(({ createBatch, changeCreateBatch }, ref) => {
  const { selectedCompany } = useCompanyContext();
  const { rolesAcess } = useRolesAccessContext();
  const access = rolesAcess?.find(
    (access) => access.report === 'create_payment_batch'
  );
  const { watch } = useFormContext<fetchAccountsPayablesFilters>();
  const [showHistory, setShowHistory] = useState(false);
  const filters = watch();
  const enabled = !!selectedCompany;

  const Unapproved = checkPermissions<APUnapprovedTableProps>({
    sectionsToAccess: ['unapproved_invoices'],
    Children: APUnapprovedTable,
  });
  const OpenPayables = checkPermissions<APOpenTableProps>({
    sectionsToAccess: ['open_payables', 'create_payment_batch'],
    Children:
      getSource(selectedCompany) === CompanySource.ViewPoint
        ? APOpenTable
        : APYardiOpenTable,
  });

  const { data, isFetching, isError } = useQuery({
    queryKey: ['getAPPayables', selectedCompany, filters],
    queryFn: ({ signal }) => {
      const { submittedID, ...otherFilters } = filters;
      return fetchAccountsPayablesData(
        {
          ...otherFilters,
          company: selectedCompany!,
          edit: submittedID ? undefined : !!access?.editable,
          draftID: submittedID,
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: enabled,
  });

  const submittedPaymentBatches = useQuery({
    queryKey: [
      'getSubmittedPaymentBatches',
      selectedCompany,
      filters.reportType,
    ],
    queryFn: ({ signal }) =>
      fetchSubmittedPaymentBatches(
        selectedCompany!,
        filters.reportType === 'openPayablesPerJob'
          ? BatchType.job
          : BatchType.payType,
        signal
      ),
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const afterApproval = (draftID: string) => {
    submittedPaymentBatches.data?.forEach(
      (draft: PaymentBatchSummary & { disabled?: boolean }) => {
        if (draft.id === draftID) {
          draft.disabled = true;
        }
      }
    );
  };

  if (isFetching || !enabled)
    return (
      <div className="text-center mx-auto mt-3">
        <ProgressSpinner />
      </div>
    );

  if (isError)
    return (
      <ErrorMessage
        content={'Failed to obtain data! Please try again later.'}
      />
    );

  if (filters.reportType === 'unapprovedInvoices') {
    return <Unapproved accountsPayables={data} />;
  }

  return (
    <ProcessContextProvider
      defaultValues={{
        enableSubmit: data?.batch ? !data.batch.submitted : false,
        enableApprove: data?.batch ? !data.batch.approved : false,
      }}
    >
      {data?.batch && createBatch && (
        <React.Fragment>
          <div
            className="text-18px mx-3 font-bold cursor-pointer totalColor hover:underline flex w-fit"
            onClick={() => setShowHistory(true)}
          >
            Check Draft Approvals
          </div>
          <ApprovalHistory
            show={showHistory}
            hide={() => {
              setShowHistory(false);
            }}
            approvals={data.batch.approvals ?? []}
            lastStep={data.batch.lastStep ?? 0}
          />
        </React.Fragment>
      )}
      <OpenPayables
        accountsPayables={processPayableList(data?.openPayables ?? [])}
        isArchive={false}
        createBatch={createBatch}
        changeCreateBatch={changeCreateBatch}
        draftID={data.batch?.id}
        defaultBatchPayables={data.batch?.payments.map(
          (payment: PaymentBatch) => {
            const { id, ...otherFields } = payment;
            return otherFields;
          }
        )}
        approvalMode={!!filters.submittedID}
        afterApproval={afterApproval}
      />
    </ProcessContextProvider>
  );
});

export default APOpenTableContainer;
